import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Slide,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Chip
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { styled, keyframes } from '@mui/system';
import ScrollToTopButton from '../Button/ScrollToTopButton';
import HomeIcon from '@mui/icons-material/Home';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PrintIcon from '@mui/icons-material/Print';


const slideInFromLeft = keyframes`
  0% {
    transform: translateX(-20px);
    opacity: 0;
  }
  100% {
    transform: translateX(0);
    opacity: 1;
  }
`;

const AnimatedTypography = styled(Typography)(({ theme }) => ({
    animation: `${slideInFromLeft} 0.5s ease-out`,
}));

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const Outstanding = () => {
    const [user, setUser] = useState({});
    const [token, setToken] = useState(() => localStorage.getItem('token') || null);
    const [fetchedData, setFetchedData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchProfile = async () => {
            const token = localStorage.getItem("token");

            if (!token) {
                navigate("/"); // Redirect if no token
                return;
            }

            try {
                const response = await axios.get("https://admin.bizstor.lk/public/api/profile", {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setUser(response.data);
            } catch (error) {
                if (error.response && error.response.status === 401) {
                    localStorage.removeItem("token");
                    navigate("/");
                }
            }
        };

        fetchProfile();
    }, []);

    useEffect(() => {
        if (user.id) {
            fetchAllData();
        }
    }, [user.id]);

    const fetchAllData = async () => {
        setLoading(true);
        try {
            const [expenseRes, paymentRes] = await Promise.all([
                axios.get('https://admin.bizstor.lk/public/api/get-expense-data'),
                axios.get('https://admin.bizstor.lk/public/api/get-expensespayment')
            ]);

            if (user.id) {
                const userExpenses = expenseRes.data.filter(expense =>
                    expense.usertoken.toLowerCase() === user.id.toString().toLowerCase()
                );
                const creditExpenses = userExpenses.filter(expense => expense.paymenttype === "Credit");
                setFetchedData(userExpenses);
                setFilteredData(creditExpenses);

                const userPayments = paymentRes.data.filter(payment =>
                    payment.usertoken.toLowerCase() === user.id.toString().toLowerCase()
                );
                setPayments(userPayments);
            } else {
                setPayments(paymentRes.data);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };


    const calculateTotalAmount = (items) => {
        const total = items.reduce((total, item) => total + Number(item.amount), 0);
        return total.toFixed(2);
    };

    const getExpensePayments = (expenseId) => {
        const expensePayments = payments.filter(payment => payment.expenses_id === expenseId);
        return expensePayments.reduce((total, payment) => total + parseFloat(payment.amount || 0), 0);
    };

    const calculateRemainingAmount = (expense) => {
        const totalExpense = expense.items && Array.isArray(expense.items)
            ? parseFloat(calculateTotalAmount(expense.items))
            : parseFloat(expense.amount || 0);

        const totalPaid = getExpensePayments(expense.id);
        return (totalExpense - totalPaid).toFixed(2);
    };

    const calculateTotalCredit = () => {
        return filteredData.reduce((total, expense) => {
            let expenseTotal = 0;
            if (expense.items && Array.isArray(expense.items)) {
                expenseTotal = parseFloat(calculateTotalAmount(expense.items));
            } else {
                expenseTotal = parseFloat(expense.amount || 0);
            }

            const paidAmount = getExpensePayments(expense.id);
            const remaining = expenseTotal - paidAmount;

            return total + (remaining > 0 ? remaining : 0);
        }, 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    };

    const groupByPerson = () => {
        const grouped = {};

        filteredData.forEach(expense => {
            const fullname = expense.fullname;
            if (!grouped[fullname]) {
                grouped[fullname] = {
                    total: 0,
                    paid: 0,
                    remaining: 0
                };
            }

            let expenseTotal = 0;
            if (expense.items && Array.isArray(expense.items)) {
                expenseTotal = parseFloat(calculateTotalAmount(expense.items));
            } else {
                expenseTotal = parseFloat(expense.amount || 0);
            }

            const paidAmount = getExpensePayments(expense.id);
            const remaining = expenseTotal - paidAmount;

            grouped[fullname].total += expenseTotal;
            grouped[fullname].paid += paidAmount;
            grouped[fullname].remaining += remaining > 0 ? remaining : 0;
        });

        return grouped;
    };

    const handleBackClick = () => {
        navigate("/allreport");
    };

    const handlePrint = () => {
        const printContents = document.getElementById('print-section').innerHTML;
        const printWindow = window.open('', '_blank');

        const htmlContent = `
        <html>
            <head>
                <title>Outstanding Report</title>
                <style>
                    body {
                        font-family: Arial, sans-serif;
                        margin: 20px;
                    }
                    table {
                        width: 100%;
                        border-collapse: collapse;
                        margin-top: 20px;
                    }
                    th, td {
   
                        padding: 8px;
                        text-align: left;
                    }
                    th {
                        background-color: #f2f2f2;
                        font-weight: bold;
                        border-bottom: 1px solid black;
                    }
                    .total {
                        margin-top: 20px;
                        font-size: 18px;
                        font-weight: bold;
                        color: red;
                    }
                    h2 {
                        text-align: center;
                        text-transform: uppercase;
                    }
                    h4, h5 {
                        text-align: center;
                    }
                    .no-print {
                        display: none;
                    }
                </style>
            </head>
            <body>
            <h2>${user.companyname ? user.companyname : ""}</h2>
                    <h4>Outstanding Report</h4>
                ${printContents}
                <div class="total">Total Outstanding: LKR. ${calculateTotalCredit()}</div>
            </body>
        </html>
    `;

        printWindow.document.write(htmlContent);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
        printWindow.close();
    };


    return (
        <Box
            sx={{ mt: 10 }}
        >
            <AnimatedTypography
                component="h1"
                sx={{
                    ml: { xs: 2, sm: 3 },
                    mt: { xs: 2, sm: 3 },
                    color: '',
                    letterSpacing: '0.01em',
                    fontSize: { xs: '20px', sm: '24px' }, // Responsive font size
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
                    <Typography
                        component="span"
                        sx={{
                            display: { xs: 'none', sm: 'inline' },
                            fontSize: { xs: '14px', sm: '16px' },
                            fontWeight: 'bold',
                            color: '',
                        }}
                    >
                        Outstanding
                    </Typography>
                    {/* Hide separator, icon, and text on small screens */}
                    <Typography
                        component="span"
                        sx={{
                            display: { xs: 'none', sm: 'inline' }, // Hidden on xs
                            ml: 1,
                            fontSize: '14px',
                            color: '',
                        }}
                    >
                        |
                    </Typography>
                    <HomeIcon
                        sx={{
                            ml: 1,
                            fontSize: '18px',
                            color: '#6d7081',
                        }}
                    />
                    <Typography
                        component="span"
                        sx={{
                            ml: 0.5,
                            fontSize: '14px',
                            color: '#6d7081',
                        }}
                    >
                        - Report - Outstanding
                    </Typography>
                </Box>
            </AnimatedTypography>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    width:  {sm:'95%',xs:'90%'},
                    maxWidth: 'auto',
                    margin: 'auto',
                    padding: '10px',
                    bgcolor: 'background.paper',
                    border: '1px solid #EDF2F7',
                    borderRadius: '16px',
                    marginTop: '30px',
                    position: 'relative',
                    '@media (max-width: 600px)': {
                        marginTop: '10px',
                    },
                }}>
                <Box>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: { xs: 'center', sm: 'flex-start' },
                            '@media (max-width: 600px)': {
                                margin: 'auto',
                            },
                            ml: { xs: 0, sm: 2 },
                            textAlign: { xs: 'center', sm: 'left' },
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: { xs: 'center', sm: 'space-between' },
                                alignItems: 'center',
                                width: '100%',
                                flexWrap: 'wrap',
                                gap: 1,
                            }}
                        >
                            <Box sx={{ color: '#1e293b' }}>
                                <h2>Outstanding</h2>
                            </Box>
                            <Box sx={{ display: { xs: 'none', sm: 'flex' } }}>
                                <IconButton onClick={handlePrint}>
                                    <PrintIcon />
                                </IconButton>
                            </Box>
                        </Box>
                        <Box
                            component="div"
                            sx={{
                                mb: { sm: 2, xs: 'none' },
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                color: 'primary.main',
                                width: 'fit-content',
                                '&:hover': {
                                    color: 'primary.main',
                                },
                            }}
                            onClick={handleBackClick}
                        >
                            <ArrowBackIcon fontSize="small" />
                            <Typography
                                variant="body2"
                                sx={{
                                    ml: 0.5,
                                    fontWeight: 500
                                }}
                            >
                                Back to Report
                            </Typography>

                        </Box>
                        <Box
                            sx={{
                                display: { xs: 'flex', sm: 'none' },
                                justifyContent: 'center',
                            }}
                        >
                            <IconButton onClick={handlePrint}>
                                <PrintIcon />
                            </IconButton>

                        </Box>

                    </Box>
                    {/**<TableContainer  >
                                <Table sx={{ minWidth: 650 }} aria-label="expense table">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell><Typography fontWeight="bold">Full Name</Typography></TableCell>
                                            <TableCell><Typography fontWeight="bold">Total Amount</Typography></TableCell>
                                            <TableCell><Typography fontWeight="bold">Paid</Typography></TableCell>
                                            <TableCell><Typography fontWeight="bold">Remaining</Typography></TableCell>
                                            <TableCell><Typography fontWeight="bold">Date</Typography></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {filteredData.length > 0 ? (
                                            filteredData.map((expense, index) => {
                                                const totalPaid = getExpensePayments(expense.id);
                                                const totalAmount = expense.items && Array.isArray(expense.items)
                                                    ? parseFloat(calculateTotalAmount(expense.items))
                                                    : parseFloat(expense.amount || 0);
                                                const remaining = calculateRemainingAmount(expense);

                                                return (
                                                    <TableRow key={index} hover>
                                                        <TableCell>{expense.fullname}</TableCell>
                                                        <TableCell>LKR.{totalAmount.toFixed(2)}</TableCell>
                                                        <TableCell>LKR.{totalPaid.toFixed(2)}</TableCell>
                                                        <TableCell>
                                                            <Typography
                                                                color={parseFloat(remaining) <= 0 ? 'success.main' : 'error.main'}
                                                                fontWeight="medium"
                                                            >
                                                                LKR.{remaining}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>{expense.date}</TableCell>
                                                    </TableRow>
                                                );
                                            })
                                        ) : (
                                            <TableRow>
                                                <TableCell colSpan={5} align="center">No credit expenses found</TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>*/}

                    <Box id="print-section" sx={{ mt: 4, p: 1, }}>
                        <TableContainer>
                            <Table aria-label="person totals table">
                                <TableHead>
                                    <TableRow
                                        sx={{
                                            '& td, & th': { padding: '8px 12px', bgcolor: '#F7FAFC' },
                                        }}
                                    >
                                        <TableCell><Typography fontWeight="bold">Person</Typography></TableCell>
                                        <TableCell><Typography fontWeight="bold">Total</Typography></TableCell>
                                        <TableCell><Typography fontWeight="bold">Paid</Typography></TableCell>
                                        <TableCell><Typography fontWeight="bold">Remaining</Typography></TableCell>
                                        <TableCell className="no-print"><Typography fontWeight="bold">Status</Typography></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {Object.keys(groupByPerson()).length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={5} align="center">
                                                No data available.
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        Object.entries(groupByPerson()).map(([person, data], index) => (
                                            <TableRow
                                                key={index}
                                                hover
                                                sx={{
                                                    '& td, & th': { padding: '8px 12px' },
                                                }}
                                            >
                                                <TableCell>{person}</TableCell>
                                                <TableCell>LKR.{data.total.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</TableCell>
                                                <TableCell>LKR.{data.paid.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</TableCell>
                                                <TableCell sx={{ color: data.remaining !== 0.00 ? 'red' : 'inherit' }}>
                                                    LKR.{data.remaining.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                                                </TableCell>
                                                <TableCell className="no-print" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    {data.remaining <= 0 ? (
                                                        <>
                                                            <CheckCircleIcon color="success" sx={{ fontSize: '16px' }} />
                                                            <span>Paid</span>
                                                        </>
                                                    ) : (
                                                        <Chip label="Pending" color="warning" size="small" />
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                        <Box sx={{ p: 2, }}>
                            <Typography variant="subtitle1" fontWeight="bold">
                                Total Outstanding: <Box component="span" sx={{ color: 'error.main' }}>LKR.{calculateTotalCredit()}</Box>
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {loading && (
                    <Box sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'white',
                        zIndex: 9999
                    }}>
                        <l-trio
                            size="50"
                            speed="1.3"
                            color="red"
                        ></l-trio>
                    </Box>
                )}
            </Box>
            <ScrollToTopButton />
        </Box>
    );
};

export default Outstanding;